import _ from 'lodash';
import React from 'react';
import produce, { original, isDraft } from 'immer';
import {
	BBCommonProps,
	BBEditableModeProps,
	BBInheritanceParent,
	BBModel,
	BBStates,
	ComputedComponentPropsFn,
	ComputedValueFn,
	EditableStateInfo,
	StorySymbols,
} from 'types/story';
import { clientLog } from 'common/utils/helpers';
import { orderMediaQueryConfig } from 'common/utils/story-media-query';
import { getMediaKeysInOrderOfInheritance } from 'common/utils/get-media-keys-in-order-of-inheritance';
import {
	CARD_ROOT_ELEMENT_INDEX,
	COMPONENT_STATES,
	COMPONENT_TYPE,
	CONTENT_ALIGN_Y,
	UNIFORM_PROPS,
	PIN_TYPE,
} from 'common/constants';
import { StateDOMAttrubutesManager } from 'client/components/common/BuildingBlocks/StateDOMAttrubutesManager';
import { getElementZIndex } from 'client/components/pages/Story/get-element-z-index';
import { TraverseTree, CreateComponentProps, MapChildren, MapComponent } from './traverse-tree-types';
import { TRAVERSE_TREE_TARGETS, CHILDREN_KEY } from './common';
import { COMPONENTS } from './components';

const log = clientLog.extend('traverseTree');

export const getElementSymbol = (elementOrigin, symbols: StorySymbols) => {
	let symbol;
	const { masterId, instanceId, childId } = _.get(elementOrigin, 'symbol', {});

	if (masterId && instanceId && !childId) {
		const { master } = symbols[masterId];
		const instance = symbols[masterId].instance[instanceId];

		// merge computed props and override with an element
		symbol = _.merge({}, master, instance);
		log('getElementSymbol', { elementOrigin, symbol });
	}

	return symbol || elementOrigin;
};

/**
 * Render whole tree
 */
export const traverseTree: TraverseTree = (tree, options) => {
	const orderedMediaQueryConfig = orderMediaQueryConfig(options.mediaQuery, {
		defaultFirst: true,
		minOrder: 'asc',
		maxOrder: 'desc',
	});

	const mediaKeysInOrderOfInheritance = getMediaKeysInOrderOfInheritance(
		options.mediaQuery.defaultPlatform,
		options.mediaQuery.config[options.currentMediaQuery],
		orderedMediaQueryConfig
	);

	/**
	 * @param element Card element
	 * @param index
	 * @param path path to children in 'Card.elements', like "0.children.1.children.0"
	 * @param idPath path to children in 'Card.elements' by uiConfig.nodeProps.id of element
	 * @param parent collection of parent elements with their properties, which are may be inherited.
	 */
	function createComponent({ element, path, idPath, index, parent = [] }: CreateComponentProps) {
		const { type, uiConfig, children } = element;

		const state =
			options.target === TRAVERSE_TREE_TARGETS.EDITOR
				? // @ts-expect-error ts-migrate FIXME
				  getElementState(options.state, { element, idPath })
				: COMPONENT_STATES.DEFAULT;

		type Props = Pick<
			BBCommonProps,
			| 'type'
			| '_id'
			| 'uiConfig'
			| 'mediaQuery'
			| 'currentMediaQuery'
			| 'isCardTree'
			| 'symbol'
			| 'getComputedComponentProps'
			| 'contentAlignY'
			| 'editableModeProps'
		> & { key: string };

		// todo: get rid of type assertion
		const Component = COMPONENTS[type]().view as React.ElementType<Props>;

		const _getComputedComponentProps = (s: BBStates) =>
			getComputedComponentProps({ element, mediaKeysInOrderOfInheritance, state: s });
		const componentProps = _getComputedComponentProps(state);

		const props: Props = {
			type,
			key: element._id,
			_id: element._id,
			uiConfig: {
				...uiConfig,
				componentProps: componentProps.value,
				nodeProps: produce(uiConfig.nodeProps, draft => {
					_.set(draft, 'data-bb', type);

					if (_.isUndefined(_.get(draft, 'style.zIndex'))) {
						_.set(
							draft,
							'style.zIndex',
							getElementZIndex({
								element,
								index,
								parent,
								isCardTree: options.isCardTree,
								cardElements: options.cardElements,
							})
						);
					}
				}),
			},
			mediaQuery: options.mediaQuery,
			currentMediaQuery: options.currentMediaQuery,
			isCardTree: options.isCardTree,
			symbol: element.symbol,
			getComputedComponentProps: _getComputedComponentProps,
		};

		// note:
		//  This is sort of "unicorn". Some of the props are assigned to the Card BB,
		//  but should be used by Content BB (contentHeight, contentAlignY). consider about better solution.
		if (type === COMPONENT_TYPE.CONTENT) {
			props.contentAlignY = _.get(
				getComputedValue({
					source: tree[CARD_ROOT_ELEMENT_INDEX[COMPONENT_TYPE.CARD]].uiConfig.componentProps.other,
					mediaKeysInOrderOfInheritance,
					currState: state,
				}),
				'value.contentAlignY',
				CONTENT_ALIGN_Y.top
			);
		}

		// Create parent collection for a component children
		const childrenParent = produce(parent, draft => {
			const prevParent: BBInheritanceParent = {
				_id: props._id,
				path,
				type,
				name: uiConfig.editorProps.name,
				inheritance: { componentProps: componentProps.inheritance as any }, // todo: no any
			};

			draft.push(prevParent);
		});

		if (options.target === TRAVERSE_TREE_TARGETS.EDITOR) {
			const editableModeProps: BBEditableModeProps = {
				nodeProps: {
					'data-path': path,
					'data-pin': _.get(componentProps.value, `other.${PIN_TYPE}`),
					'data-lock': !uiConfig.editorProps.selectable,
				},
				eventListeners: {
					onMouseDown: options.onMouseDown,
					onFocus: options.onFocus,
					onMouseEnter: options.onMouseEnter,
					onMouseLeave: options.onMouseLeave,
					onMouseDownCapture: options.onMouseDownCapture,
					onDoubleClickCapture: options.onDoubleClickCapture,
				},
				inheritance: {
					componentProps: componentProps.inheritance,
					parent,
				},
			};

			// Assign state attr
			if (state !== COMPONENT_STATES.DEFAULT) {
				const { SELECTED_HOVER, SELECTED, HOVER } = COMPONENT_STATES;
				const compoundSelectors: BBStates[] = [SELECTED_HOVER];
				const isCompoundSelector = compoundSelectors.includes(state);

				if (isCompoundSelector) {
					// 💡💡💡 If you're adding a new compound selector,
					// you've to add his implementation in "<StyleRenderer />" and getComputedValue too
					if (state === SELECTED_HOVER) {
						const { attributes } = new StateDOMAttrubutesManager({
							[SELECTED]: true,
							[HOVER]: true,
							[SELECTED_HOVER]: true,
						});
						_.assign(editableModeProps.nodeProps, attributes);
					}
				} else {
					const { attributes } = new StateDOMAttrubutesManager({ [state]: true });
					_.assign(editableModeProps.nodeProps, attributes);
				}
			}

			// log('editableModeProps', editableModeProps.inheritance.parent);

			return (
				<Component editableModeProps={editableModeProps} {...props}>
					{mapChildren({ children, path, idPath, parent: childrenParent })}
				</Component>
			);
		}

		return <Component {...props}>{mapChildren({ children, path, idPath, parent: childrenParent })}</Component>;
	}

	/**
	 * @param children Card element(s)
	 * @param path path to children in 'Card.elements', like "0.children.1.children.0"
	 * @param idPath path to children in 'Card.elements' by uiConfig.nodeProps.id of element
	 * @param parent collection of parent elements with their properties, which are may be inherited.
	 */
	function mapChildren({ children, path, idPath, parent }: MapChildren): any {
		if (children && Array.isArray(children)) {
			// BBModel[]
			return children.map((elementOrigin, index) => {
				// @ts-expect-error ts-migrate FIXME
				const element = getElementSymbol(elementOrigin, options.symbols);
				const nextPath = `${path}.${CHILDREN_KEY}.${index}`;
				const nextIdPath = `${idPath}.${getNodeId(element)}`;

				return createComponent({
					element,
					index,
					path: nextPath,
					idPath: nextIdPath,
					parent,
				});
			});
		}

		// Render text node "{ state: { platform: '<p>Woven silk pyjamas.</p>' } }"
		return getComputedValue({
			source: children,
			mediaKeysInOrderOfInheritance,
			currState: COMPONENT_STATES.DEFAULT, // Always default, text cannot be changed by state
		}).value;
	}

	/**
	 * @param element {Object}
	 * @param index {Number}
	 * @param path {String}
	 * @param idPath {String}
	 * @return {[null]}
	 */
	function mapComponent({ element, index, path, idPath }: MapComponent) {
		return [createComponent({ element, index, path, idPath, parent: [] })];
	}

	log('traverseTree map');
	return tree.map((element, index) => {
		const path = `${index}`;
		const idPath = getNodeId(element);

		return mapComponent({
			element,
			index,
			path,
			idPath,
		});
	});
};

/**
 * Get value computed from component state and current media query
 *
 * @param props.source component object or component property value
 * @param props.sourceKey component property key
 * @param props.mediaKeysInOrderOfInheritance media query keys array in order of inheritance
 * @param props.currState current state key
 */
const getComputedValue: ComputedValueFn = props => {
	const { source, sourceKey = '', mediaKeysInOrderOfInheritance, currState } = props;
	const currentStates: string[] = [];

	// 💡💡💡 If you're adding a new compound state,
	// you've to add his implementation in "<StyleRenderer />", and traverseTree too
	const compoundStates = [COMPONENT_STATES.SELECTED_HOVER];
	if ((compoundStates as string[]).includes(currState)) {
		switch (currState) {
			case COMPONENT_STATES.SELECTED_HOVER:
				currentStates.push(COMPONENT_STATES.SELECTED, COMPONENT_STATES.HOVER, COMPONENT_STATES.SELECTED_HOVER);
				break;
			default:
				break;
		}
	} else {
		currentStates.push(currState);
	}
	const orderedStates = _.uniq(_.concat(COMPONENT_STATES.DEFAULT, currentStates));

	let value;
	let inheritance;
	const hasValue = v => v !== '' && !_.isUndefined(v);

	if (typeof source === 'object' && source !== null && !Array.isArray(source) && _.isEmpty(source)) {
		value = source;
	}

	_.forEach(orderedStates, state => {
		_.forEach(mediaKeysInOrderOfInheritance, query => {
			const nextValue = _.get(
				source,
				[state, query],
				sourceKey === 'styles' || sourceKey === 'other' ? {} : undefined
			);

			if (!nextValue) {
				// Universal properties (not written for state or media query and without inheritance)
				if (UNIFORM_PROPS[sourceKey]) {
					value = source;
				}

				return;
			}

			// log(`Update (${state})`, value, 'to', nextValue);

			const shouldInheritOwnValues: boolean = _.isObject(nextValue) && sourceKey !== 'animation';

			// Regular properties (eg. styles, other).
			// Checks each key in nextValue and store value and inheritance for it
			if (shouldInheritOwnValues) {
				if (!value) {
					value = {};
				}

				_.forOwn(nextValue, (v, k) => {
					if (!inheritance) {
						inheritance = {};
					}

					// Store inheritance
					const ik = _.get(inheritance, k, []);
					if (hasValue(v)) {
						ik.push({
							secondValue: _.get(value, k),
							value: v,
							state,
							mq: query,
						});
					}
					_.set(inheritance, k, ik);

					// Store computed value
					_.set(value, k, v);
				});
			}
			// Irregular properties (e.g. animation or Text BB children).
			// Doesn't check each key in nextValue, but store it whole
			else {
				// Store inheritance
				if (!inheritance) {
					inheritance = [];
				}
				if (hasValue(nextValue)) {
					inheritance.push({
						secondValue: value,
						value: nextValue,
						state,
						mq: query,
					});
				}

				// Store computed value
				value = nextValue;
			}
		});
	});

	return {
		value,
		inheritance,
	};
};

/**
 * Get computed values of Card element "uiConfig.componentProps" object
 *
 * @param element Card element
 * @param state current state of the element
 * @param mediaKeysInOrderOfInheritance ordered story media query keys in order of inheritance
 */

const getComputedComponentProps: ComputedComponentPropsFn = ({ element, mediaKeysInOrderOfInheritance, state }) => {
	const inheritance: ReturnType<ComputedComponentPropsFn>['inheritance'] = {
		styles: {},
		other: {},
	};

	const componentProps = produce(element.uiConfig.componentProps, draft => {
		_.forOwn(draft, (value, key) => {
			const computedValue = getComputedValue({
				source: isDraft(value) ? original(value)! : value,
				sourceKey: isDraft(key) ? original(key) || '' : key,
				mediaKeysInOrderOfInheritance,
				currState: state,
			});

			// Store computed value
			_.set(draft, key, computedValue.value);

			// Store inheritance data
			inheritance[key] = computedValue.inheritance;
		});
	});

	return {
		value: componentProps,
		inheritance,
	};
};

/**
 * @param state {String} current state transmitted from admin panel
 * @param source {Object} info about element to which applied state
 * @param element {Object} building block (element) model
 * @param idPath {String}
 * @return {string|*}
 */
function getElementState(
	{ state, source }: EditableStateInfo,
	{ element, idPath = '' }: { element: BBModel; idPath: string }
) {
	const match = _.includes(idPath, source.id);
	if (match) {
		return state;
	}
	return COMPONENT_STATES.DEFAULT;
}

function getNodeId(element: BBModel) {
	return element.uiConfig.nodeProps.id;
}
